"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  OptimizerQueryState,
  OptimizerStickyFilterScope,
  OptimizerStickyFilterState,
  OptimizerStickyPersistedFilters,
} from "./query-state";
import {
  extractStickyFilterState,
  coerceOptimizerStickyPayload,
  stickyFilterStatesEqual,
  hasStickyFiltersActive,
  applyStickyFilterState,
} from "./query-state";
import { safeGet, safeSet, safeRemove } from "@/components/onboarding/safe-storage";
import { isLinkVisit } from "@/lib/tool-link";
import {
  saveOptimizerStickyFilterState,
  clearOptimizerStickyFilterState,
} from "@/lib/actions/optimizer-sticky-filters";
import type { InitialOptimizerStickyHydration } from "@/lib/optimizer-server-state";

const STORAGE_KEY_PREFIX = "upsider_optimizer_sticky_filters";

function getLocalKey(scope: OptimizerStickyFilterScope, userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${scope}:${userId}`;
}

function readLocal(key: string): OptimizerStickyPersistedFilters | null {
  const raw = safeGet(key);
  if (!raw) return null;
  try {
    return coerceOptimizerStickyPayload(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function useStickyOptimizerFilters({
  initialUserId,
  scope,
  state,
  initialHydration,
  applyHydratedState,
  hasUserInteractedRef,
  sanitizeStickyState,
}: {
  initialUserId?: string;
  scope?: OptimizerStickyFilterScope;
  state: OptimizerQueryState;
  initialHydration?: InitialOptimizerStickyHydration;
  applyHydratedState: (nextState: OptimizerQueryState, options: { replaceUrl: boolean; fetch: boolean }) => void;
  hasUserInteractedRef: React.RefObject<boolean>;
  sanitizeStickyState?: (stickyState: OptimizerStickyFilterState) => OptimizerStickyFilterState;
}): { hydrationComplete: boolean; forgetSavedFilters: () => void } {
  const effectiveUserId = initialUserId ?? "";
  const effectiveScope = scope ?? "optimizer";
  const effectiveHydration: InitialOptimizerStickyHydration = initialHydration ?? {
    scope: "optimizer",
    hasExplicitStickyUrlParams: true,
    serverPayload: null,
  };
  const [hydrationComplete, setHydrationComplete] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingServerWriteRef = useRef<(() => Promise<void>) | null>(null);
  const prevStickyRef = useRef<OptimizerStickyFilterState | null>(null);
  const promoteSavedAtRef = useRef<string | null>(null);
  const loadedSavedRef = useRef(false);
  const linkVisitRef = useRef(false);

  const sanitize = useCallback(
    (sticky: OptimizerStickyFilterState): OptimizerStickyFilterState =>
      sanitizeStickyState ? sanitizeStickyState(sticky) : sticky,
    [sanitizeStickyState],
  );

  function sanitizePayload(
    payload: OptimizerStickyPersistedFilters | null,
  ): OptimizerStickyPersistedFilters | null {
    return payload && { ...payload, state: sanitize(payload.state) };
  }

  useEffect(() => {
    linkVisitRef.current = isLinkVisit(window.location.search);
    if (!effectiveUserId || effectiveHydration.hasExplicitStickyUrlParams) {
      setHydrationComplete(true);
      return;
    }

    const localKey = getLocalKey(effectiveScope, effectiveUserId);
    const localPayload = sanitizePayload(readLocal(localKey));
    const serverPayload = sanitizePayload(effectiveHydration.serverPayload);

    const localTime = localPayload?.savedAt ? new Date(localPayload.savedAt).getTime() : 0;
    const serverTime = serverPayload?.savedAt ? new Date(serverPayload.savedAt).getTime() : 0;

    if (localPayload && localTime > serverTime && !hasUserInteractedRef.current) {
      const nextState = applyStickyFilterState(state, localPayload.state);
      applyHydratedState(nextState, { replaceUrl: true, fetch: true });
      promoteSavedAtRef.current = localPayload.savedAt;
      loadedSavedRef.current = true;
    } else if (serverPayload) {
      safeSet(localKey, JSON.stringify(serverPayload));
      loadedSavedRef.current = true;
    }

    setHydrationComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistNow = useCallback((stickyState: OptimizerStickyFilterState, savedAt = new Date().toISOString()) => {
    if (!effectiveUserId || linkVisitRef.current) return;

    const hasActive = hasStickyFiltersActive(stickyState);
    const localKey = getLocalKey(effectiveScope, effectiveUserId);
    const payload: OptimizerStickyPersistedFilters = {
      v: 1,
      savedAt,
      state: stickyState,
    };

    if (hasActive) {
      safeSet(localKey, JSON.stringify(payload));
    } else {
      safeRemove(localKey);
    }

    const serverWrite = async () => {
      try {
        if (hasActive) {
          await saveOptimizerStickyFilterState(effectiveScope, payload);
        } else {
          await clearOptimizerStickyFilterState(effectiveScope);
        }
      } catch (err) {
        console.error("[StickyFilters] Server save failed:", err);
      }
    };
    pendingServerWriteRef.current = serverWrite;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      pendingServerWriteRef.current = null;
      serverWrite();
    }, 600);
  }, [effectiveScope, effectiveUserId]);

  useEffect(() => {
    if (!hydrationComplete) return;

    const current = extractStickyFilterState(state);
    const sticky = sanitize(current);
    if (!prevStickyRef.current) {
      prevStickyRef.current = current;
      if (promoteSavedAtRef.current) persistNow(sticky, promoteSavedAtRef.current);
      return;
    }

    if (stickyFilterStatesEqual(sanitize(prevStickyRef.current), sticky)) return;

    prevStickyRef.current = current;
    persistNow(sticky);
  }, [state, hydrationComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const forgetSavedFilters = useCallback(() => {
    if (!loadedSavedRef.current) return;
    loadedSavedRef.current = false;
    prevStickyRef.current = extractStickyFilterState(stateRef.current);
    persistNow(sanitize(prevStickyRef.current));
  }, [persistNow, sanitize]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (pendingServerWriteRef.current) {
        pendingServerWriteRef.current();
        pendingServerWriteRef.current = null;
      }
    };
  }, []);

  return { hydrationComplete, forgetSavedFilters };
}
